import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import {
  Modal,
  Backdrop,
  Fade,
  Paper,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  makeStyles
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import { CMSAPIWithToken } from "../../../apis/CMS";
import { SHOW_LOADER, HIDE_LOADER } from "../../../action_creators/actioncreator";
import { Api } from "../../../config/api";
import * as Yup from "yup";
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex"
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 4, 3),
    width: 600,
    borderRadius: "0"
  },
  button: {
    margin: "30px 0 40px"
  },
  formControl: {
    width: "100%",
    marginTop: "5px"
  },
  btnColor: {
    background: theme.palette.buttonColor.main,
    "&:hover": {
      background: theme.palette.buttonColor.main
    }
  },
  title: {
    margin: "20px 0",
    textTransform: "capitalize"
  },
  CloseIcon: {
    color: "#000",
    cursor: "pointer",
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  helperText: {
    marginTop: "20px",
    color: "#7D7D7D"
  },
  textField: {
    marginTop: theme.spacing(2),
  },
  dropdownGrid: {
    marginBottom: theme.spacing(2),
  },
  submitButton: {
    backgroundColor: "#00AEEF",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#00AEEF",
    },
  },
  menuItem: {
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "#f5f5f5", // Light gray color for selected item
    }
  },
  error: {
    borderColor: 'red',
  }
}));

const validationSchema = Yup.object({
  from: Yup.string().required("Identifier is required"),
  to: Yup.string().required("Identifier is required")
    .notOneOf([Yup.ref('from'), null], "Identifiers cannot be the same"),
});

const ContentCopyModal = ({ open, handleClose, learningIdentifiers, sandboxIdentities }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [toOptions, setToOptions] = useState(learningIdentifiers);
  const [toSelection, setToSelection] = useState("learning");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState('success'); 
  const [notificationMessage, setNotificationMessage] = useState('');

 
  useEffect(() => {
    if (toSelection === "learning") {
      setToOptions(learningIdentifiers);
    } else {
      setToOptions(sandboxIdentities);
    }
  }, [toSelection, learningIdentifiers, sandboxIdentities]);

  const handleToSelectionChange = (event) => {
    setToSelection(event.target.value);
  };

  const formik = useFormik({
    initialValues: {
      from: "",
      to: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        dispatch(SHOW_LOADER());
        const payload = {
          from: values.from,
          to: values.to,
          fromType: 'learning',
          toType: toSelection,
        };
        await CMSAPIWithToken().post(Api.convert_content, payload);
        
        setNotificationType('success');
        setNotificationMessage('Content copied successfully!');
        setShowNotification(true);
        setTimeout(() => {
          handleClose(); // Close modal after 2 seconds
        }, 2000);
        // Optionally show a success message here
      } catch (error) {
        console.error("Error submitting form: ", error);
        setNotificationType('error');
        setNotificationMessage('Failed to copy content. Please try again.');
        setShowNotification(true);
        // Optionally show an error message here
      } finally {
        setSubmitting(false);
        dispatch(HIDE_LOADER());
      }
    },
  });

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={open}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={open}>
          <div className={classes.paper}>
            <CloseIcon className={classes.CloseIcon} onClick={handleClose} />
            <Typography variant="h4" className={classes.title}>
              Learning Content Copy
            </Typography>
            <Typography variant="body2" className={classes.helperText}>
              Please fill all the details
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              <Grid container className={classes.dropdownGrid} spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" className={classes.formControl} error={formik.touched.from && Boolean(formik.errors.from)}>
                    <InputLabel>From</InputLabel>
                    <Select
                      value="Learning"
                      label="From"
                      readOnly
                    >
                      <MenuItem value="Learning">Learning</MenuItem>
                    </Select>
                  </FormControl>
                 <FormControl
                  className={classes.formControl}
                  style={{ marginTop: "15px" }}
                  error={formik.touched.from && Boolean(formik.errors.from)}
                >
                  <Autocomplete
                    options={learningIdentifiers}
                    value={formik.values.from || null}
                    onChange={(event, newValue) => {
                      formik.setFieldValue("from", newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Identifier"
                        variant="outlined"
                        error={formik.touched.from && Boolean(formik.errors.from)}
                        helperText={formik.touched.from && formik.errors.from}
                      />
                    )}
                  />
                </FormControl>

                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" className={classes.formControl} error={formik.touched.to && Boolean(formik.errors.to)}>
                    <InputLabel id="to-selection-label">To</InputLabel>
                    <Select
                      labelId="to-selection-label"
                      value={toSelection}
                      onChange={handleToSelectionChange}
                      label="To"
                      name="toSelection"
                      MenuProps={{
                        MenuListProps: {
                          style: {
                            backgroundColor: '#f5f5f5', // Light gray color
                          },
                        },
                      }}
                    >
                      <MenuItem value="learning" className={classes.menuItem}>Learning</MenuItem>
                      <MenuItem value="sandbox" className={classes.menuItem}>Sandbox</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl
                      className={classes.formControl}
                      style={{ marginTop: "15px" }}
                      error={formik.touched.to && Boolean(formik.errors.to)}
                    >
                      <Autocomplete
                        options={toOptions}
                        value={formik.values.to || null}
                        onChange={(event, newValue) => {
                          formik.setFieldValue("to", newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Identifier"
                            variant="outlined"
                            error={formik.touched.to && Boolean(formik.errors.to)}
                            helperText={formik.touched.to && formik.errors.to}
                          />
                        )}
                      />
                    </FormControl>

                </Grid>
              </Grid>

              <Grid container spacing={2} className={classes.button}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    className={classes.submitButton}
                    fullWidth
                    type="submit"
                    disabled={formik.isSubmitting}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </form>
            {showNotification && (
            <Alert
              onClose={() => setShowNotification(false)}
              severity={notificationType}
              style={{ marginTop: '16px' }}
            >
              {notificationMessage}
            </Alert>
          )}
          </div>
        </Fade>
      </Modal>
    </div>
  );
};

export default ContentCopyModal;
